import { clsx } from 'clsx'
import { type FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { OfferV1OfferTypeEnum } from '@redteclab/api/clients/bully'

import { Notification } from 'base-ui'

import { productAccessorGetSellerPromotionFromOffer } from '../../../../product'
import { productAccessorGetPharmaBlockedAlternativeText } from '../../../model/productAccessor/productAccessorGetPharmaBlockedAlternativeText'
import { productAccessorHasZavaOdsPartner } from '../../../model/productAccessor/productAccessorHasZavaOdsPartner'
import { productAccessorIsPrescriptionProduct } from '../../../model/productAccessor/productAccessorIsPrescriptionProduct'
import { productAccessorIsProductNotInCatalog } from '../../../model/productAccessor/productAccessorIsProductNotInCatalog'
import { productAccessorIsProductOutOfSale } from '../../../model/productAccessor/productAccessorIsProductOutOfSale'
import { productAccessorIsProductOutOfStock } from '../../../model/productAccessor/productAccessorIsProductOutOfStock'
import { PRODUCT_TEMPLATES } from '../../../model/productTemplates'
import { ProductDescriptionParsedField } from '../../ProductDescription/ProductDescriptionParsedField'
import { useProductPageContext } from '../../productPageContext'
import { ProductPageOdsDialogCtaButton } from '../../ProductPageOdsDialog/ProductPageOdsDialogCtaButton'
import { ProductPageVolumePricingBadge } from '../../ProductPageVolumePricingBadge/ProductPageVolumePricingBadge'
import { useShouldRenderVolumePricingLabel } from '../../ProductPageVolumePricingBadge/useShouldRenderVolumePricingBadge'
import { ProductPrescriptionTypeSelect } from '../../ProductPrescriptionType'
import { ProductBuyBoxSellerPromotionBadge } from '../ProductBuyBoxSellerPromotionBadge/ProductBuyBoxSellerPromotionBadge'
import { useHideAddToCart } from '../useHideAddToCart'

import { ProductAddToCartButtonRow } from './ProductAddToCartButtonRow'
import { ProductAddToCartGroupPriceInfo } from './ProductAddToCartGroupPriceInfo/ProductAddToCartGroupPriceInfo'

const useShallOdsButtonRender = (): boolean => {
  const { currentVariant, productPageOdsConfig, productTemplate } =
    useProductPageContext()

  return (
    productTemplate === PRODUCT_TEMPLATES.RX &&
    productAccessorHasZavaOdsPartner(currentVariant) &&
    productPageOdsConfig !== null
  )
}

const useRenderPrescriptionSelect = (): boolean => {
  const { currentOffer, currentVariant, productTemplate } =
    useProductPageContext()

  if (
    productAccessorIsProductOutOfStock(currentVariant.stock_status) ||
    productAccessorIsProductOutOfSale(currentVariant.stock_status) ||
    productAccessorIsProductNotInCatalog(currentVariant.stock_status)
  ) {
    return false
  }

  const isRx = productTemplate === PRODUCT_TEMPLATES.RX

  return currentOffer?.offerType
    ? isRx && currentOffer.offerType === OfferV1OfferTypeEnum.Sae
    : isRx
}

const useShouldRenderPharmaBlockedAlternativeText = (): boolean => {
  const { product } = useProductPageContext()

  if (
    !productAccessorIsProductOutOfStock(product.stock_status) ||
    !productAccessorIsProductOutOfSale(product.stock_status) ||
    !productAccessorIsProductNotInCatalog(product.stock_status)
  ) {
    return true
  }

  return false
}

export const ProductAddToCartGroup: FC = () => {
  const hideAddToCart = useHideAddToCart()
  const i18n = useIntl()
  const { product } = useProductPageContext()
  const renderPrescriptionSelect = useRenderPrescriptionSelect()
  const isPrescription = productAccessorIsPrescriptionProduct(product)
  const shouldShowOdsButton = useShallOdsButtonRender()
  const pharmaBlockedAlternativeText =
    productAccessorGetPharmaBlockedAlternativeText(product)
  const renderPharmaBlockedAlternativeText =
    useShouldRenderPharmaBlockedAlternativeText()
  const renderVolumeBasedPricingLabel = useShouldRenderVolumePricingLabel()
  const { currentOffer } = useProductPageContext()

  const sellerPromotion =
    currentOffer && productAccessorGetSellerPromotionFromOffer(currentOffer)

  if (hideAddToCart) {
    return null
  }

  return (
    <div className="flex flex-col gap-2">
      {sellerPromotion || renderVolumeBasedPricingLabel ? (
        <p className="text-l font-medium">
          <FormattedMessage id="seller.promotion.product.discount.buybox" />
        </p>
      ) : null}
      <div className="flex flex-wrap">
        {renderVolumeBasedPricingLabel ? (
          <ProductPageVolumePricingBadge />
        ) : null}
        {sellerPromotion ? (
          <ProductBuyBoxSellerPromotionBadge
            offer={currentOffer}
            product={product}
            sellerPromotion={sellerPromotion}
          />
        ) : null}
      </div>
      <div
        className={clsx(
          'mb-2.5 gap-2.5',
          renderPrescriptionSelect
            ? 'grid grid-cols-3 tablet:grid-cols-2'
            : 'flex',
        )}
      >
        {renderPrescriptionSelect ? (
          <ProductPrescriptionTypeSelect className="col-span-2 tablet:col-auto" />
        ) : null}

        <ProductAddToCartButtonRow
          renderPrescriptionSelect={renderPrescriptionSelect}
        />
      </div>

      {isPrescription &&
      renderPharmaBlockedAlternativeText &&
      pharmaBlockedAlternativeText ? (
        <Notification
          className="my-4 bg-light-primary-low"
          severity="info"
          title={i18n.formatMessage({
            id: 'product.details.pharmaBlockedAlternativeText',
          })}
        >
          <ProductDescriptionParsedField
            field={pharmaBlockedAlternativeText.toString()}
          />
        </Notification>
      ) : null}

      {shouldShowOdsButton ? <ProductPageOdsDialogCtaButton /> : null}
      <ProductAddToCartGroupPriceInfo />
    </div>
  )
}
